'use client';

import { c as _c } from "react/compiler-runtime";
import { Avatar, AvatarFallback, cn, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import React from 'react';
import type { ChainId } from '../web3.types';
import { getChainConfig, isChainId } from '../utils';
import * as ChainIcons from './icons/chains';
interface ChainIconProps {
  /** Chain id for which to render an icon */
  chainId: ChainId | number | string;
  className?: string;
  /** When true, wraps the icon in a tooltip showing the chain short name */
  showTooltip?: boolean;
}
function getChainFirstLetter(chainId: ChainId | number | string): string {
  const id = typeof chainId === 'number' ? chainId : Number(chainId);
  const chainName = isChainId(id) ? getChainConfig(id).shortName : '';
  return chainName.charAt(0) || 'C';
}
function FallbackChainIcon(t0) {
  const $ = _c(9);
  const {
    chainId,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("h-4 w-4 rounded-full", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== chainId) {
    t2 = getChainFirstLetter(chainId);
    $[2] = chainId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <AvatarFallback>{t2}</AvatarFallback>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <Avatar className={t1}>{t3}</Avatar>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function getIconComponent(iconName: string): React.ComponentType<ChainIconProps> | undefined {
  const iconKey = `${iconName.replace(/\s+/g, '')}Icon` as keyof typeof ChainIcons;
  // oxlint-disable-next-line import/namespace -- lookup key is derived at runtime
  return ChainIcons[iconKey] as React.ComponentType<ChainIconProps> | undefined;
}
export function ChainIcon(t0) {
  const $ = _c(29);
  const {
    chainId,
    className,
    showTooltip
  } = t0;
  const id = typeof chainId === "number" ? chainId : Number(chainId);
  let config;
  let icon;
  if ($[0] !== chainId || $[1] !== className || $[2] !== id) {
    config = isChainId(id) ? getChainConfig(id) : undefined;
    const iconName = config?.shortName ?? "";
    if (!iconName) {
      let t1;
      if ($[5] !== className) {
        t1 = cn("h-4 w-4 rounded-full", className);
        $[5] = className;
        $[6] = t1;
      } else {
        t1 = $[6];
      }
      let t2;
      if ($[7] !== chainId || $[8] !== t1) {
        t2 = <FallbackChainIcon chainId={chainId} className={t1} />;
        $[7] = chainId;
        $[8] = t1;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      icon = t2;
    } else {
      const IconComponent = getIconComponent(iconName);
      if (!IconComponent) {
        let t1;
        if ($[10] !== className) {
          t1 = cn("h-4 w-4 rounded-full", className);
          $[10] = className;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        let t2;
        if ($[12] !== chainId || $[13] !== t1) {
          t2 = <FallbackChainIcon chainId={chainId} className={t1} />;
          $[12] = chainId;
          $[13] = t1;
          $[14] = t2;
        } else {
          t2 = $[14];
        }
        icon = t2;
      } else {
        const isSquare = (config as {
          isIconSquare?: boolean;
        }).isIconSquare;
        const t1 = !isSquare;
        let t2;
        if ($[15] !== className || $[16] !== t1) {
          t2 = cn("h-4 w-4", className, {
            "rounded-full": t1
          });
          $[15] = className;
          $[16] = t1;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        let t3;
        if ($[18] !== IconComponent || $[19] !== chainId || $[20] !== t2) {
          t3 = <IconComponent chainId={chainId} className={t2} />;
          $[18] = IconComponent;
          $[19] = chainId;
          $[20] = t2;
          $[21] = t3;
        } else {
          t3 = $[21];
        }
        icon = t3;
      }
    }
    $[0] = chainId;
    $[1] = className;
    $[2] = id;
    $[3] = config;
    $[4] = icon;
  } else {
    config = $[3];
    icon = $[4];
  }
  if (!showTooltip) {
    return icon;
  }
  let t1;
  if ($[22] !== icon) {
    t1 = <TooltipTrigger asChild={true}><span>{icon}</span></TooltipTrigger>;
    $[22] = icon;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  const t2 = config?.shortName ?? "Unknown";
  let t3;
  if ($[24] !== t2) {
    t3 = <TooltipContent>{t2}</TooltipContent>;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== t1 || $[27] !== t3) {
    t4 = <Tooltip>{t1}{t3}</Tooltip>;
    $[26] = t1;
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
}